import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, User, Stethoscope, GraduationCap, MapPin, Calendar, Briefcase, FileCheck, ClipboardCheck, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';
import SEO from '../components/SEO';

import Step1Personal from '../components/onboarding/Step1Personal';
import Step2Professional from '../components/onboarding/Step2Professional';
import Step3Education from '../components/onboarding/Step3Education';
import Step4Practice from '../components/onboarding/Step4Practice';
import Step5Availability from '../components/onboarding/Step5Availability';
import Step6Experience from '../components/onboarding/Step6Experience';
import Step7Documents from '../components/onboarding/Step7Documents';
import Step8Review from '../components/onboarding/Step8Review';

const STEPS = [
  { id: 1, key: 'personal', title: 'Personal Information', shortTitle: 'Personal', icon: User, required: true, component: Step1Personal },
  { id: 2, key: 'professional', title: 'Professional Details', shortTitle: 'Professional', icon: Stethoscope, required: true, component: Step2Professional },
  { id: 3, key: 'education', title: 'Education', shortTitle: 'Education', icon: GraduationCap, required: false, component: Step3Education },
  { id: 4, key: 'practice', title: 'Practice Locations', shortTitle: 'Practice', icon: MapPin, required: false, component: Step4Practice },
  { id: 5, key: 'availability', title: 'Availability', shortTitle: 'Availability', icon: Calendar, required: false, component: Step5Availability },
  { id: 6, key: 'experience', title: 'Experience', shortTitle: 'Experience', icon: Briefcase, required: false, component: Step6Experience },
  { id: 7, key: 'documents', title: 'Documents & Verification', shortTitle: 'Documents', icon: FileCheck, required: true, component: Step7Documents },
  { id: 8, key: 'review', title: 'Review & Submit', shortTitle: 'Review', icon: ClipboardCheck, required: true, component: Step8Review },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [searchParams] = useSearchParams();

  // Support deep-linking to a specific step via ?step=N — used by dashboard "Edit" shortcuts
  useEffect(() => {
    const requested = parseInt(searchParams.get('step') || '', 10);
    if (requested >= 1 && requested <= STEPS.length) {
      setCurrentStep(requested);
    }
    // Only react to the initial ?step= param — user can navigate freely after
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [completedSteps, setCompletedSteps] = useState({});
  const [formData, setFormData] = useState({
    personal: {},
    professional: {},
    education: [],
    practice: [],
    availability: {},
    experience: [],
    documents: {},
    review: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!['doctor', 'nurse'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadExistingProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadExistingProfile = async () => {
    try {
      const data = await apiRequest('/auth/me');
      // Prefill formData from existing user + provider_profile data
      const profile = data.profile || {};
      setFormData(prev => ({
        ...prev,
        personal: {
          first_name: (data.user?.name || '').split(' ')[0] || '',
          last_name: (data.user?.name || '').split(' ').slice(1).join(' ') || '',
          email: data.user?.email || '',
          primary_mobile: data.user?.phone || '',
          gender: profile.gender || '',
          date_of_birth: profile.date_of_birth || '',
          languages: profile.languages || [],
          bio: profile.bio || '',
          address_line1: profile.address_line1 || '',
          country: profile.country || 'India',
          state: profile.state || '',
          city: profile.location || '',
          pin_code: profile.pin_code || '',
        },
        professional: {
          role: data.user?.role || 'doctor',
          medical_registration_number: profile.medical_registration_number || profile.license_number || '',
          primary_specialization: profile.specialization || '',
          experience_years: profile.experience_years || '',
          services: profile.services || [],
          qualification: profile.qualification || '',
          bio: profile.bio || '',
        },
        education: profile.education_history || [],
        practice: profile.practice_locations || (profile.clinic_name ? [{
          clinic_name: profile.clinic_name,
          address: profile.clinic_address,
          consultation_fee: profile.consultation_fee,
          home_visit_fee: profile.home_visit_fee,
          is_current: true,
        }] : []),
        availability: profile.availability || {},
        experience: profile.experience_history || [],
        documents: {
          id_proof: profile.id_proof_document,
          license: profile.license_document,
          degree_cert: profile.certificates?.[0],
          registration_cert: profile.certificates?.[1],
          avatar: data.user?.avatar,
          bank_details: profile.bank_details || null,
        },
      }));
      // Auto-mark steps as completed if they have data
      const done = {};
      if (profile.specialization) done[2] = true;
      if (profile.education_history?.length) done[3] = true;
      if (profile.clinic_name || profile.practice_locations?.length) done[4] = true;
      if (profile.availability && Object.keys(profile.availability).length) done[5] = true;
      if (profile.experience_history?.length) done[6] = true;
      if (profile.id_proof_document || profile.certificates?.length) done[7] = true;
      if (profile.onboarding_completed) done[8] = true;
      setCompletedSteps(done);
    } catch (e) {
      console.error('Failed to load profile:', e);
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = STEPS.length;
  const progress = Math.round(((currentStep - 1) / totalSteps) * 100);
  const completionPercent = Math.round((Object.keys(completedSteps).length / totalSteps) * 100);

  const updateStepData = (stepKey, data) => {
    setFormData(prev => ({ ...prev, [stepKey]: data }));
  };

  const handleSaveAndContinue = async (stepData) => {
    setSaving(true);
    const currentStepConfig = STEPS[currentStep - 1];
    if (currentStepConfig.key === 'review') {
      // no state update needed
    } else if (currentStepConfig.key === 'documents') {
      // Merge to preserve doc URLs already uploaded via /provider/upload-document
      setFormData(prev => ({ ...prev, documents: { ...prev.documents, ...stepData } }));
    } else {
      updateStepData(currentStepConfig.key, stepData);
    }
    try {
      // Persist to backend based on step
      await persistStepData(currentStepConfig.key, stepData);
      setCompletedSteps(prev => ({ ...prev, [currentStep]: true }));
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        toast.success('Onboarding submitted! We will verify your profile within 24 hours.');
        navigate('/provider');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      navigate('/provider');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const jumpToStep = (stepId) => {
    if (stepId <= currentStep || completedSteps[stepId]) {
      setCurrentStep(stepId);
      window.scrollTo(0, 0);
    }
  };

  const persistStepData = async (stepKey, data) => {
    // Map wizard data to backend provider_profile update
    let payload = {};
    if (stepKey === 'personal') {
      // Update user name + provider profile personal fields
      const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
      if (fullName) {
        await apiRequest('/patient/profile', { method: 'PUT', body: JSON.stringify({ name: fullName }) }).catch(() => {});
      }
      payload = {
        gender: data.gender,
        date_of_birth: data.date_of_birth,
        languages: data.languages,
        bio: data.bio,
        address_line1: data.address_line1,
        country: data.country,
        state: data.state,
        location: data.city,
        pin_code: data.pin_code,
      };
    } else if (stepKey === 'professional') {
      payload = {
        specialization: data.primary_specialization,
        medical_registration_number: data.medical_registration_number,
        experience_years: data.experience_years ? parseInt(data.experience_years) : null,
        services: data.services,
        qualification: data.qualification,
        bio: data.bio,
      };
    } else if (stepKey === 'education') {
      payload = { education_history: data };
    } else if (stepKey === 'practice') {
      payload = {
        practice_locations: data,
        // Also mirror first clinic to legacy fields for backward compat
        clinic_name: data[0]?.clinic_name,
        clinic_address: data[0]?.address,
        consultation_fee: data[0]?.consultation_fee ? parseFloat(data[0].consultation_fee) : null,
        home_visit_fee: data[0]?.home_visit_fee ? parseFloat(data[0].home_visit_fee) : null,
      };
    } else if (stepKey === 'availability') {
      // Save to provider_profiles.availability JSONB column via profile PUT
      // (Note: PUT /provider/availability is only a toggle-active endpoint — do not use)
      payload = { availability: data };
    } else if (stepKey === 'experience') {
      payload = { experience_history: data };
    } else if (stepKey === 'documents') {
      // Documents are uploaded separately via /provider/upload-document endpoint by the step component
      payload = { bank_details: data.bank_details || null };
    } else if (stepKey === 'review') {
      // Mark onboarding as completed
      payload = { onboarding_completed: true };
    }

    // Clean payload (remove undefined/null)
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    if (Object.keys(clean).length > 0) {
      await apiRequest('/provider/profile', { method: 'PUT', body: JSON.stringify(clean) });
    }
  };

  const StepComponent = STEPS[currentStep - 1]?.component;
  const currentStepConfig = STEPS[currentStep - 1];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3B1C73]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO path="/provider/onboarding" title="Complete Your Professional Profile" description="Set up your PHYSIGN doctor/nurse profile" />

      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <span className="text-2xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="physign-text-logo">Physign</span>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-xs text-slate-500">Profile Completion</p>
              <p className="text-sm font-semibold text-[#3B1C73]">{completionPercent}%</p>
            </div>
            <button
              onClick={() => { logout(); navigate('/auth'); }}
              className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar Stepper (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Onboarding Progress</h3>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#3B1C73]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-2">Step {currentStep} of {totalSteps}</p>
            </div>
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isDone = completedSteps[step.id];
              const isClickable = step.id <= currentStep || isDone;
              return (
                <button
                  key={step.id}
                  onClick={() => jumpToStep(step.id)}
                  disabled={!isClickable}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                    isActive ? 'bg-[#3B1C73] text-white shadow-md' :
                    isDone ? 'bg-white text-slate-700 hover:bg-slate-100' :
                    'text-slate-400 hover:bg-slate-100'
                  } ${!isClickable && 'cursor-not-allowed opacity-60'}`}
                  data-testid={`step-nav-${step.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-white/20' : isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100'
                  }`}>
                    {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs uppercase tracking-wider ${isActive ? 'text-white/70' : 'text-slate-400'}`}>Step {step.id}</p>
                    <p className="font-medium text-sm truncate">{step.shortTitle}</p>
                  </div>
                  {step.required && !isDone && (
                    <span className={`text-xs ${isActive ? 'text-white/70' : 'text-red-500'}`}>*</span>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Mobile progress */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">Step {currentStep} of {totalSteps}</p>
              <p className="text-sm font-semibold text-[#3B1C73]">{completionPercent}%</p>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <motion.div className="h-full bg-[#3B1C73]" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step Header */}
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#3B1C73]/10 text-[#3B1C73] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
                  Step {currentStep} of {totalSteps}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {currentStepConfig.title}
                </h1>
                <p className="text-slate-500 mt-1">Fill in the details below or skip and complete later.</p>
              </div>

              {/* Step Component (rounded card) */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                {StepComponent && (
                  currentStepConfig.key === 'review' ? (
                    <StepComponent
                      formData={formData}
                      onJumpTo={jumpToStep}
                      onSubmit={() => handleSaveAndContinue({})}
                      saving={saving}
                    />
                  ) : (
                    <StepComponent
                      data={formData[currentStepConfig.key]}
                      onSave={handleSaveAndContinue}
                      onChange={(d) => updateStepData(currentStepConfig.key, d)}
                      userRole={user?.role}
                      saving={saving}
                    />
                  )
                )}
              </div>

              {/* Navigation buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1 || saving}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-slate-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="wizard-back-btn"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex flex-col sm:flex-row gap-3">
                  {!currentStepConfig.required && (
                    <button
                      onClick={handleSkip}
                      disabled={saving}
                      className="px-5 py-2.5 rounded-xl text-slate-600 hover:bg-white disabled:opacity-40"
                      data-testid="wizard-skip-btn"
                    >
                      Skip for now
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const form = document.getElementById(`step-form-${currentStep}`);
                      if (form) form.requestSubmit();
                      else handleSaveAndContinue(formData[currentStepConfig.key]);
                    }}
                    disabled={saving}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-[#3B1C73] text-white font-semibold hover:bg-[#2c1457] disabled:opacity-60 shadow-md hover:shadow-lg transition-all"
                    data-testid="wizard-next-btn"
                  >
                    {saving ? 'Saving...' : currentStep === totalSteps ? 'Submit for Verification' : 'Save & Continue'}
                    {!saving && <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
